import React, { useEffect, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { Card, Box, useMediaQuery, Typography, Grid, List, ListItem, ListItemAvatar, ListItemText, Avatar, Divider, TextField, InputAdornment } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import YieldTrendChart from './YieldTrendChart';
import InputUsageChart from './InputUsageChart';
import HarvestsChart from './HarvestsChart';
import MetricsCard from './MetricsCard';
import { useFarmData } from '../context/FarmDataContext';
import CurrentCropsTable from './CurrentCropsTable';
import ActivityLogTable from './ActivityLogTable';
import TimelineEvents from './TimelineEvents';
import SearchIcon from '@mui/icons-material/Search';
import InputBase from '@mui/material/InputBase';
import { alpha } from '@mui/material/styles';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './react-datepicker-dark.css';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const CustomDateInput = React.forwardRef(({ value, onClick, placeholder, height = 32 }, ref) => (
  <TextField
    value={value}
    onClick={onClick}
    inputRef={ref}
    placeholder={placeholder}
    size="small"
    sx={{
      width: 150,
      bgcolor: '#222C2F',
      borderRadius: 1,
      input: {
        color: '#AAB4C8',
        fontWeight: 400,
        fontSize: 14,
        height: height,
        padding: '4px 8px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
      },
      '& fieldset': { border: 'none' },
      mr: 0.5,
      height: height,
      p: 0,
      boxShadow: 'none !important',
      minHeight: height,
      maxHeight: height,
    }}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <CalendarTodayIcon sx={{ color: '#AAB4C8', fontSize: 16 }} />
        </InputAdornment>
      ),
      readOnly: true,
    }}
  />
));

const FarmDetailsPage = () => {
    const theme = useTheme();
    const location = useLocation();
    const { idToken, farm_id } = useOutletContext();
    const { farmData, fetchFarmData, loading } = useFarmData();
    const [salesView, setSalesView] = useState("zone_id");
    const [tabIndex, setTabIndex] = useState(0);
    const [selectedPlantType, setSelectedPlantType] = useState(null);
    const [search, setSearch] = useState('');
    const [plantTypes, setPlantTypes] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const section_id = location.state?.section_id;
    const plot_id = location.state?.plot_id;
    const crop_id = location.state?.crop_id;

    const isMobile = useMediaQuery('(max-width: 600px)');

    // Always use zones from farmData
    const zones = farmData?.zones || [];
    
    // Debug logging
    console.log('FarmDetails farmData:', {
        hasFarmData: !!farmData,
        cropsLength: farmData?.crops?.length,
        harvestsLength: farmData?.harvests?.length,
        zonesLength: farmData?.zones?.length,
        fertilizerEventsLength: farmData?.fertilizerEvents?.length,
        fungicideEventsLength: farmData?.fungicideEvents?.length,
        insecticideEventsLength: farmData?.insecticideEvents?.length,
        herbicideEventsLength: farmData?.herbicideEvents?.length,
        zonesData: farmData?.zones,
        locationStateZones: location.state?.zones
    });

    // Get selected crop's date range
    const selectedCrop = farmData?.crops?.find(c => c.id === crop_id);
    const cropDateRange = selectedCrop ? {
        start: new Date(selectedCrop.transplant_date),
        end: new Date(selectedCrop.last_harvest_date)
    } : null;

    // Get startDate and endDate from Outlet context (provided by FarmPageLayout)
    const outletContext = useOutletContext();
    const startDate = outletContext?.startDate;
    const endDate = outletContext?.endDate;
    const setStartDate = outletContext?.setStartDate;
    const setEndDate = outletContext?.setEndDate;

    const handleYearChange = (year) => {
        setSelectedYear(year);
    };

    // Fetch data using the context
    useEffect(() => {
        console.log('FarmDetails useEffect triggered:', {
            idToken: !!idToken,
            farm_id,
            hasFarmData: !!farmData,
            farmDataKeys: farmData ? Object.keys(farmData) : null,
            cropsLength: farmData?.crops?.length,
            harvestsLength: farmData?.harvests?.length,
            zonesLength: farmData?.zones?.length
        });
        
        if (idToken && farm_id) {
            if (!farmData) {
                console.log('FarmDetails: No farmData, fetching...');
                fetchFarmData(farm_id, idToken);
            } else {
                console.log('FarmDetails: Using existing farmData');
            }
        }
    }, [farm_id, idToken, farmData, fetchFarmData]);

    // Extract plant types dynamically
    useEffect(() => {
        if (farmData) {
            const types = Array.from(
                new Set(farmData.crops.map(crop => crop.plant_type_cl || crop.plant_type || "Unknown"))
            );
            setPlantTypes(types);
            if (!selectedPlantType && types.length > 0) {
                setSelectedPlantType(types[0]); // Default to the first plant type
            }
        }
    }, [farmData, selectedPlantType]);

    const availableYears = Array.from(
        new Set([
            ...farmData.sales.map(sale => new Date(sale.date).getFullYear()),
            ...farmData.harvests.map(harvest => new Date(harvest.date).getFullYear()),
            ...farmData.fertilizerEvents.map(event => new Date(event.date).getFullYear()),
            ...farmData.crops.map(crop => new Date(crop.planting_date).getFullYear())
        ])
    ).sort((a, b) => b - a);

    // Helper function to filter data by section/plot and date range
    const filterByLocation = (data) => {
        if (!data) return [];
        return data.filter(item => {
            // First check if item is deleted
            if (item.deleted) return false;
            
            // Check crop filter first (highest priority)
            if (crop_id) {
                // For harvests and sales, check if they belong to the selected crop
                if (item.crop_id && item.crop_id !== crop_id) return false;
                // For sales, check if the harvest belongs to the selected crop
                if (item.harvest_id) {
                    const harvest = farmData?.harvests?.find(h => h.id === item.harvest_id);
                    if (harvest && harvest.crop_id !== crop_id) return false;
                }
            }
            
            // Check location filters
            if (plot_id) {
                if (item.plot_id !== plot_id) return false;
            } else if (section_id) {
                if (item.zone_id !== section_id) return false;
            }

            // Check date range - prioritize global date range over cropDateRange
            if (item.date) {
                const itemDate = new Date(item.date);
                // Always use the global startDate and endDate (set by crop selection)
                if (startDate && endDate) {
                    if (itemDate < startDate || itemDate > endDate) {
                        return false;
                    }
                } else if (cropDateRange) {
                    // Fallback to cropDateRange only if global dates are not set
                    if (itemDate < cropDateRange.start || itemDate > cropDateRange.end) {
                        return false;
                    }
                }
            }

            return true;
        });
    };

    // Calculate total yield
    const calculateTotalYield = () => {
        const filteredHarvests = filterByLocation(farmData.harvests);
        const totalYield = filteredHarvests.reduce((sum, harvest) => sum + (harvest.harvest_amt || 0), 0);
        return {
            value: totalYield.toFixed(1),
            unit: 'kg'
        };
    };

    // Calculate total sales
    const calculateTotalSales = () => {
        const filteredSales = filterByLocation(farmData.sales);
        const totalSales = filteredSales.reduce((sum, sale) => sum + (sale.sale_amt || 0), 0);
        return {
            value: totalSales,
            unit: 'currency'
        };
    };

    // Helper function to filter crops by location and date range
    const filterCropsByLocation = (crops) => {
        if (!crops) return [];
        return crops.filter(crop => {
            // First check if item is deleted
            if (crop.deleted) return false;
            
            // Check crop filter first (highest priority)
            if (crop_id) {
                if (crop.id !== crop_id) return false;
            }
            
            // Check location filters
            if (plot_id) {
                if (crop.plot_id !== plot_id) return false;
            } else if (section_id) {
                if (crop.zone_id !== section_id) return false;
            }

            // Check date range - prioritize global date range over cropDateRange
            if (crop.planting_date) {
                const itemDate = new Date(crop.planting_date);
                // Always use the global startDate and endDate (set by crop selection)
                if (startDate && endDate) {
                    if (itemDate < startDate || itemDate > endDate) {
                        return false;
                    }
                } else if (cropDateRange) {
                    // Fallback to cropDateRange only if global dates are not set
                    if (itemDate < cropDateRange.start || itemDate > cropDateRange.end) {
                        return false;
                    }
                }
            }

            return true;
        });
    };

    // Calculate total costs
    const calculateTotalCosts = () => {
        const eventTypes = ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents'];
        const totalCosts = eventTypes.reduce((sum, eventType) => {
            const filteredEvents = filterByLocation(farmData[eventType]);
            return sum + filteredEvents.reduce((eventSum, event) => eventSum + (event.event_cost || 0), 0);
        }, 0);
        
        // Add seed costs from crops
        const filteredCrops = filterCropsByLocation(farmData.crops);
        const seedCosts = filteredCrops.reduce((sum, crop) => sum + (crop.seed_cost || 0), 0);
        
        return {
            value: totalCosts + seedCosts,
            unit: 'currency'
        };
    };

    // Calculate net profit
    const calculateNetProfit = () => {
        const filteredSales = filterByLocation(farmData.sales);
        const totalSales = filteredSales.reduce((sum, sale) => sum + (sale.sale_amt || 0), 0);
        
        const eventTypes = ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents'];
        const totalCosts = eventTypes.reduce((sum, eventType) => {
            const filteredEvents = filterByLocation(farmData[eventType]);
            return sum + filteredEvents.reduce((eventSum, event) => eventSum + (event.event_cost || 0), 0);
        }, 0);
        
        // Add seed costs from crops
        const filteredCrops = filterCropsByLocation(farmData.crops);
        const seedCosts = filteredCrops.reduce((sum, crop) => sum + (crop.seed_cost || 0), 0);

        const netProfit = totalSales - totalCosts - seedCosts;
        return {
            value: netProfit,
            unit: 'currency'
        };
    };

    // Helper to generate trend data for a metric by year
    const getYearlyTrend = (data, valueKey) => {
        // Only include non-deleted and filtered by location
        const filtered = filterByLocation(data);
        const byYear = {};
        filtered.forEach(item => {
            const d = new Date(item.date);
            const year = d.getFullYear();
            if (!byYear[year]) byYear[year] = 0;
            byYear[year] += item[valueKey] || 0;
        });
        // Return sorted array of {x, y}
        return Object.keys(byYear).sort().map(year => ({ x: year, y: byYear[year] }));
    };

    // Helper to get percent change between the current date range and the previous period of the same length
    const getPercentChange = (data, valueKey) => {
        if (!startDate || !endDate) return { percent: 0, isIncrease: true };
        
        // Calculate the same date range from the previous year
        const prevStart = new Date(startDate);
        const prevEnd = new Date(endDate);
        prevStart.setFullYear(prevStart.getFullYear() - 1);
        prevEnd.setFullYear(prevEnd.getFullYear() - 1);

        // Sum for current range
        const currFiltered = data.filter(item => {
            const d = new Date(item.date);
            return d >= startDate && d <= endDate;
        });
        const currSum = currFiltered.reduce((sum, item) => sum + (item[valueKey] || 0), 0);

        // Sum for previous range (same dates, previous year)
        const prevFiltered = data.filter(item => {
            const d = new Date(item.date);
            return d >= prevStart && d <= prevEnd;
        });
        const prevSum = prevFiltered.reduce((sum, item) => sum + (item[valueKey] || 0), 0);

        console.log(`getPercentChange filtered data for ${valueKey}:`, {
            currFiltered,
            prevFiltered,
            currSum,
            prevSum
        });

        console.log(`getPercentChange for ${valueKey}:`, {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            prevStart: prevStart.toISOString(),
            prevEnd: prevEnd.toISOString(),
            currSum,
            prevSum,
            percent: prevSum === 0 ? (currSum === 0 ? 0 : 100) : ((currSum - prevSum) / Math.abs(prevSum)) * 100
        });

        if (prevSum === 0) {
            if (currSum === 0) return { percent: 0, isIncrease: false };
            return { percent: 100, isIncrease: true };
        }
        const percent = ((currSum - prevSum) / Math.abs(prevSum)) * 100;
        return { percent: Number(percent.toFixed(1)), isIncrease: currSum >= prevSum };
    };

    // Helper to generate trend data for a metric by month, filling all months from earliest to current
    const getMonthlyTrend = (data, valueKey) => {
        const filtered = filterByLocation(data);
        if (filtered.length === 0) return [];
        // Find earliest and latest month
        const dates = filtered.map(item => new Date(item.date));
        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));
        // Set to first of month
        minDate.setDate(1);
        maxDate.setDate(1);
        // Build all months between min and max
        const months = [];
        let d = new Date(minDate);
        while (d <= maxDate) {
            months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
            d.setMonth(d.getMonth() + 1);
        }
        // Aggregate values by month
        const byMonth = {};
        filtered.forEach(item => {
            const d = new Date(item.date);
            const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            if (!byMonth[month]) byMonth[month] = 0;
            byMonth[month] += item[valueKey] || 0;
        });
        // Return all months, filling missing with 0
        return months.map(month => ({ x: month, y: byMonth[month] || 0 }));
    };

    // Calculate yearly and monthly trends for each metric
    const yieldYearlyTrend = getYearlyTrend(farmData.harvests, 'harvest_amt');
    const yieldMonthlyTrend = getMonthlyTrend(farmData.harvests, 'harvest_amt');
    const yieldChange = getPercentChange(farmData.harvests, 'harvest_amt');

    const salesYearlyTrend = getYearlyTrend(farmData.sales, 'sale_amt');
    const salesMonthlyTrend = getMonthlyTrend(farmData.sales, 'sale_amt');
    const salesChange = getPercentChange(farmData.sales, 'sale_amt');

    const getCostYearlyTrend = () => {
        const eventTypes = ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents'];
        const byYear = {};
        eventTypes.forEach(type => {
            filterByLocation(farmData[type]).forEach(event => {
                const d = new Date(event.date);
                const year = d.getFullYear();
                if (!byYear[year]) byYear[year] = 0;
                byYear[year] += event.event_cost || 0;
            });
        });
        
        // Add seed costs by year
        filterCropsByLocation(farmData.crops).forEach(crop => {
            const d = new Date(crop.planting_date);
            const year = d.getFullYear();
            if (!byYear[year]) byYear[year] = 0;
            byYear[year] += crop.seed_cost || 0;
        });
        
        return Object.keys(byYear).sort().map(year => ({ x: year, y: byYear[year] }));
    };
    const costYearlyTrend = getCostYearlyTrend();
    const costMonthlyTrend = (() => {
        const eventTypes = ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents'];
        const allEvents = eventTypes.flatMap(type => farmData[type] || []);
        
        // Create a combined dataset with events and crops for monthly trend calculation
        const eventsWithDates = allEvents.map(event => ({
            date: event.date,
            event_cost: event.event_cost || 0
        }));
        
        const cropsWithDates = filterCropsByLocation(farmData.crops).map(crop => ({
            date: crop.planting_date,
            event_cost: crop.seed_cost || 0
        }));
        
        const allCostData = [...eventsWithDates, ...cropsWithDates];
        return getMonthlyTrend(allCostData, 'event_cost');
    })();
    const costData = [
        ...['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents']
            .flatMap(type => farmData[type] || []),
        ...farmData.crops.map(crop => ({
            date: crop.planting_date,
            event_cost: crop.seed_cost || 0
        }))
    ];
    
    console.log('Cost data being passed to getPercentChange:', costData);
    
    const costChange = getPercentChange(costData, 'event_cost');

    const getProfitYearlyTrend = () => {
        const years = Array.from(new Set([...salesYearlyTrend.map(d => d.x), ...costYearlyTrend.map(d => d.x)])).sort();
        return years.map(year => {
            const sales = salesYearlyTrend.find(d => d.x === year)?.y || 0;
            const costs = costYearlyTrend.find(d => d.x === year)?.y || 0;
            return { x: year, y: sales - costs };
        });
    };
    const profitYearlyTrend = getProfitYearlyTrend();
    const profitMonthlyTrend = (() => {
        const months = Array.from(new Set([
            ...salesMonthlyTrend.map(d => d.x),
            ...costMonthlyTrend.map(d => d.x)
        ])).sort();
        return months.map(month => {
            const sales = salesMonthlyTrend.find(d => d.x === month)?.y || 0;
            const costs = costMonthlyTrend.find(d => d.x === month)?.y || 0;
            return { x: month, y: sales - costs };
        });
    })();
    // Net profit percent change: compare (sales - costs) in current range vs previous range
    const getProfitChange = () => {
        if (!startDate || !endDate) return { percent: 0, isIncrease: true };
        
        // Calculate the same date range from the previous year
        const prevStart = new Date(startDate);
        const prevEnd = new Date(endDate);
        prevStart.setFullYear(prevStart.getFullYear() - 1);
        prevEnd.setFullYear(prevEnd.getFullYear() - 1);

        // Current period
        const currSales = farmData.sales
            .filter(item => {
                const d = new Date(item.date);
                return d >= startDate && d <= endDate;
            })
            .reduce((sum, item) => sum + (item.sale_amt || 0), 0);
        const currCosts = ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents']
            .flatMap(type => farmData[type] || [])
            .filter(item => {
                const d = new Date(item.date);
                return d >= startDate && d <= endDate;
            })
            .reduce((sum, item) => sum + (item.event_cost || 0), 0);
        
        // Add seed costs for current period
        const currSeedCosts = farmData.crops
            .filter(crop => {
                const d = new Date(crop.planting_date);
                return d >= startDate && d <= endDate;
            })
            .reduce((sum, crop) => sum + (crop.seed_cost || 0), 0);
        const currProfit = currSales - currCosts - currSeedCosts;

        // Previous period
        const prevSales = farmData.sales
            .filter(item => {
                const d = new Date(item.date);
                return d >= prevStart && d <= prevEnd;
            })
            .reduce((sum, item) => sum + (item.sale_amt || 0), 0);
        const prevCosts = ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents']
            .flatMap(type => farmData[type] || [])
            .filter(item => {
                const d = new Date(item.date);
                return d >= prevStart && d <= prevEnd;
            })
            .reduce((sum, item) => sum + (item.event_cost || 0), 0);
        
        // Add seed costs for previous period
        const prevSeedCosts = farmData.crops
            .filter(crop => {
                const d = new Date(crop.planting_date);
                return d >= prevStart && d <= prevEnd;
            })
            .reduce((sum, crop) => sum + (crop.seed_cost || 0), 0);
        const prevProfit = prevSales - prevCosts - prevSeedCosts;

        console.log('getProfitChange:', {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            prevStart: prevStart.toISOString(),
            prevEnd: prevEnd.toISOString(),
            currSales,
            currCosts,
            currSeedCosts,
            currProfit,
            prevSales,
            prevCosts,
            prevSeedCosts,
            prevProfit,
            percent: prevProfit === 0 ? (currProfit === 0 ? 0 : 100) : ((currProfit - prevProfit) / Math.abs(prevProfit)) * 100
        });

        if (prevProfit === 0) {
            if (currProfit === 0) return { percent: 0, isIncrease: false };
            return { percent: 100, isIncrease: true };
        }
        const percent = ((currProfit - prevProfit) / Math.abs(prevProfit)) * 100;
        return { percent: Number(percent.toFixed(1)), isIncrease: currProfit >= prevProfit };
    };
    const profitChange = getProfitChange();

    // Always use the year before the startDate's year for previous year
    const getPreviousYear = () => {
        if (!startDate) return null;
        return startDate.getFullYear() - 1;
    };

    // Generate metrics data
    const metrics = [
        {
            title: 'Total Yield',
            ...calculateTotalYield(),
            trendData: yieldMonthlyTrend,
            yearlyTrend: yieldYearlyTrend,
            percentChange: yieldChange.percent,
            isIncrease: yieldChange.isIncrease,
            previousYear: getPreviousYear(),
        },
        {
            title: 'Total Sales',
            ...calculateTotalSales(),
            trendData: salesMonthlyTrend,
            yearlyTrend: salesYearlyTrend,
            percentChange: salesChange.percent,
            isIncrease: salesChange.isIncrease,
            previousYear: getPreviousYear(),
        },
        {
            title: 'Total Costs',
            ...calculateTotalCosts(),
            trendData: costMonthlyTrend,
            yearlyTrend: costYearlyTrend,
            percentChange: costChange.percent,
            isIncrease: costChange.isIncrease,
            previousYear: getPreviousYear(),
        },
        {
            title: 'Net Profit',
            ...calculateNetProfit(),
            trendData: profitMonthlyTrend,
            yearlyTrend: profitYearlyTrend,
            percentChange: profitChange.percent,
            isIncrease: profitChange.isIncrease,
            previousYear: getPreviousYear(),
        },
    ];

    if (loading || !farmData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Typography variant="h6" color="text.secondary">Loading...</Typography>
            </Box>
        );
    }

    const generateStackedBarData = () => {
        const data = {};

        // List of event types
        const eventTypes = ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents'];

        // Aggregating event costs
        eventTypes.forEach((eventType) => {
            farmData[eventType].forEach((event) => {
                const date = new Date(event.date);
                const year = date.getFullYear();
                const monthYear = `${year}-${String(date.getMonth() + 1).padStart(2, '0')}`;

                if (!data[year]) {
                    data[year] = { year, fertilizerCost: 0, fungicideCost: 0, insecticideCost: 0, herbicideCost: 0, seedCost: 0 };
                }

                if (!data[monthYear]) {
                    data[monthYear] = { monthYear, fertilizerCost: 0, fungicideCost: 0, insecticideCost: 0, herbicideCost: 0, seedCost: 0 };
                }

                const eventCost = event.event_cost || 0;

                if (eventType === 'fertilizerEvents') {
                    data[year].fertilizerCost += eventCost;
                    data[monthYear].fertilizerCost += eventCost;
                } else if (eventType === 'fungicideEvents') {
                    data[year].fungicideCost += eventCost;
                    data[monthYear].fungicideCost += eventCost;
                } else if (eventType === 'insecticideEvents') {
                    data[year].insecticideCost += eventCost;
                    data[monthYear].insecticideCost += eventCost;
                } else if (eventType === 'herbicideEvents') {
                    data[year].herbicideCost += eventCost;
                    data[monthYear].herbicideCost += eventCost;
                }
            });
        });

        // Aggregating seed costs
        farmData.crops.forEach((crop) => {
            const year = new Date(crop.planting_date).getFullYear();
            const monthYear = `${year}-${String(new Date(crop.planting_date).getMonth() + 1).padStart(2, '0')}`;
            const seedCost = (crop.units_planted || 0) * (crop.unit_cost || 0);

            if (!data[year]) {
                data[year] = { year, fertilizerCost: 0, fungicideCost: 0, insecticideCost: 0, herbicideCost: 0, seedCost: 0 };
            }

            if (!data[monthYear]) {
                data[monthYear] = { monthYear, fertilizerCost: 0, fungicideCost: 0, insecticideCost: 0, herbicideCost: 0, seedCost: 0 };
            }

            data[year].seedCost += seedCost;
            data[monthYear].seedCost += seedCost;
        });

        return Object.values(data).sort((a, b) => {
            const aDate = a.year || new Date(a.monthYear).getTime();
            const bDate = b.year || new Date(b.monthYear).getTime();
            return aDate - bDate;
        });
    };

    const calculateTotalCostsAndChange = (year) => {
        const previousYear = year - 1;

        const calculateYearlyCost = (events, targetYear) => {
            return events
                .filter(event => new Date(event.date).getFullYear() === targetYear)
                .reduce((total, event) => total + (event.event_cost || 0), 0);
        };

        const currentYearCost = ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents'].reduce(
            (total, eventType) => total + calculateYearlyCost(farmData[eventType], year),
            0
        );

        const lastYearCost = ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents'].reduce(
            (total, eventType) => total + calculateYearlyCost(farmData[eventType], previousYear),
            0
        );

        const change = lastYearCost !== 0 ? ((currentYearCost - lastYearCost) / lastYearCost) * 100 : 0;

        return {
            totalCost: currentYearCost.toFixed(2),
            change: change.toFixed(1),
            isIncrease: change >= 0,
            previousYear
        };
    };

    const calculateTotalAndChange = (data, field, year) => {
        const previousYear = year - 1;

        const currentYearTotal = data
            .filter(item => new Date(item.date).getFullYear() === year)
            .reduce((total, item) => total + (item[field] || 0), 0);

        const lastYearTotal = data
            .filter(item => new Date(item.date).getFullYear() === previousYear)
            .reduce((total, item) => total + (item[field] || 0), 0);

        const change = lastYearTotal !== 0 ? ((currentYearTotal - lastYearTotal) / lastYearTotal) * 100 : 0;

        return {
            total: currentYearTotal.toFixed(2),
            change: change.toFixed(1),
            isIncrease: change >= 0,
            previousYear
        };
    };

    const calculatePostHarvestLossAndChange = (harvests, sales, year) => {
        const previousYear = year - 1;

        const yearHarvest = (year) =>
            harvests
                .filter(h => new Date(h.date).getFullYear() === year)
                .reduce((total, h) => total + (h.harvest_amt || 0), 0);

        const yearSalesHarvestAmt = (year) =>
            sales
                .filter(s => new Date(s.date).getFullYear() === year)
                .reduce((total, s) => total + (s.harvest_amt || 0), 0);

        const currentYearLoss = ((yearHarvest(year) - yearSalesHarvestAmt(year)) / yearHarvest(year)) * 100;
        const lastYearLoss = ((yearHarvest(previousYear) - yearSalesHarvestAmt(previousYear)) / yearHarvest(previousYear)) * 100;

        const change = lastYearLoss !== 0 ? ((currentYearLoss - lastYearLoss) / Math.abs(lastYearLoss)) * 100 : 0;

        return {
            totalLoss: currentYearLoss.toFixed(1),
            change: change.toFixed(1),
            isIncrease: change >= 0,
            previousYear
        };
    };

    const totalCosts = calculateTotalCostsAndChange(selectedYear);
    const totalSales = calculateTotalAndChange(farmData.sales, "sale_amt", selectedYear);
    const totalHarvests = calculateTotalAndChange(farmData.harvests, "harvest_amt", selectedYear);
    const postHarvestLoss = calculatePostHarvestLossAndChange(farmData.harvests, farmData.sales, selectedYear);


    const generateProfitabilityData = () => {
        const stackedData = {};
        const eventTypes = ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents'];
        const zonesArray = zones || []; // Provide fallback for zones

        // Process sales data
        farmData.sales.forEach(sale => {
            const date = new Date(sale.date);
            const year = date.getFullYear(); // Extract year from sale date

            const harvest = farmData.harvests.find(h => h.id === sale.harvest_id);
            const plantType = harvest?.plant_type_cl || harvest?.plant_type || "Unknown";
            const zoneId = sale.zone_id || "Unknown";
            const zoneName = zonesArray.find(zone => zone.id === zoneId)?.name || "Unknown Zone";

            // Grouping key
            const groupKey = salesView === "zone_id" ? zoneName : plantType;

            if (!stackedData[year]) {
                stackedData[year] = {};
            }

            if (!stackedData[year][groupKey]) {
                stackedData[year][groupKey] = {
                    group: groupKey,
                    year,
                    totalSales: 0,
                    fertilizerCost: 0,
                    fungicideCost: 0,
                    insecticideCost: 0,
                    herbicideCost: 0,
                    seedCost: 0,
                };
            }

            // Sum sales
            stackedData[year][groupKey].totalSales += sale.sale_amt || 0;
        });

        // Process event costs
        eventTypes.forEach(eventType => {
            farmData[eventType].forEach(event => {
                const eventDate = new Date(event.date);
                const year = eventDate.getFullYear(); // Extract year from event date
                const zoneId = event.zone_id || "Unknown";

                // Distinct plant types in the zone
                const plantTypesInZone = Array.from(
                    new Set(
                        farmData.crops
                            .filter(crop => crop.zone_id === zoneId)
                            .map(crop => crop.plant_type_cl || crop.plant_type || "Unknown")
                    )
                );

                if (salesView === "zone_id") {
                    const zoneName = zonesArray.find(zone => zone.id === zoneId)?.name || "Unknown Zone";

                    if (!stackedData[year]) {
                        stackedData[year] = {};
                    }

                    if (!stackedData[year][zoneName]) {
                        stackedData[year][zoneName] = {
                            group: zoneName,
                            year,
                            totalSales: 0,
                            fertilizerCost: 0,
                            fungicideCost: 0,
                            insecticideCost: 0,
                            herbicideCost: 0,
                            seedCost: 0,
                        };
                    }

                    const eventCost = event.event_cost || 0;

                    if (eventType === 'fertilizerEvents') {
                        stackedData[year][zoneName].fertilizerCost += eventCost;
                    } else if (eventType === 'fungicideEvents') {
                        stackedData[year][zoneName].fungicideCost += eventCost;
                    } else if (eventType === 'insecticideEvents') {
                        stackedData[year][zoneName].insecticideCost += eventCost;
                    } else if (eventType === 'herbicideEvents') {
                        stackedData[year][zoneName].herbicideCost += eventCost;
                    }
                }

                if (salesView === "plant_type") {
                    const eventCost = event.event_cost || 0;
                    const costPerPlantType = plantTypesInZone.length > 0 ? eventCost / plantTypesInZone.length : 0;

                    plantTypesInZone.forEach(plantType => {
                        if (!stackedData[year]) {
                            stackedData[year] = {};
                        }

                        if (!stackedData[year][plantType]) {
                            stackedData[year][plantType] = {
                                group: plantType,
                                year,
                                totalSales: 0,
                                fertilizerCost: 0,
                                fungicideCost: 0,
                                insecticideCost: 0,
                                herbicideCost: 0,
                                seedCost: 0,
                            };
                        }

                        if (eventType === 'fertilizerEvents') {
                            stackedData[year][plantType].fertilizerCost += costPerPlantType;
                        } else if (eventType === 'fungicideEvents') {
                            stackedData[year][plantType].fungicideCost += costPerPlantType;
                        } else if (eventType === 'insecticideEvents') {
                            stackedData[year][plantType].insecticideCost += costPerPlantType;
                        } else if (eventType === 'herbicideEvents') {
                            stackedData[year][plantType].herbicideCost += costPerPlantType;
                        }
                    });
                }
            });
        });

        // Process seed costs
        farmData.crops.forEach(crop => {
            const cropYear = new Date(crop.planting_date).getFullYear(); // Extract year from planting date
            const groupKey = salesView === "zone_id"
                ? zonesArray.find(zone => zone.id === crop.zone_id)?.name || "Unknown Zone"
                : crop.plant_type_cl || crop.plant_type || "Unknown";

            if (!stackedData[cropYear]) {
                stackedData[cropYear] = {};
            }

            if (!stackedData[cropYear][groupKey]) {
                stackedData[cropYear][groupKey] = {
                    group: groupKey,
                    year: cropYear,
                    totalSales: 0,
                    fertilizerCost: 0,
                    fungicideCost: 0,
                    insecticideCost: 0,
                    herbicideCost: 0,
                    seedCost: 0,
                };
            }

            const seedCost = (crop.units_planted || 0) * (crop.unit_cost || 0);
            stackedData[cropYear][groupKey].seedCost += seedCost;
        });

        // Convert to array format for the chart
        return Object.keys(stackedData).flatMap(year =>
            Object.values(stackedData[year]).map(item => {
                const totalCosts =
                    item.fertilizerCost +
                    item.fungicideCost +
                    item.insecticideCost +
                    item.herbicideCost +
                    item.seedCost;
                const profitability = item.totalSales
                    ? ((item.totalSales - totalCosts) / item.totalSales) * 100
                    : 0;

                return {
                    ...item,
                    totalCosts,
                    profitability: profitability.toFixed(1),
                };
            })
        );
    };

    const generateHarvestData = (selectedPlantType) => {
        if (!selectedPlantType) {
            console.error("No selectedPlantType provided.");
            return [];
        }

        const harvestData = {};

        // Map sales by harvest ID, including unit price and harvest amount
        const salesByHarvestId = farmData.sales.reduce((acc, sale) => {
            const harvest = farmData.harvests.find((h) => h.id === sale.harvest_id);
            if (harvest && (harvest.plant_type_cl || harvest.plant_type) === selectedPlantType) {
                acc[sale.harvest_id] = {
                    unitPrice: sale.unit_price || 0,
                    harvestAmt: sale.harvest_amt || 0,
                };
            }
            return acc;
        }, {});

        // Process sales for the selected plant type
        farmData.sales.forEach((sale) => {
            const harvest = farmData.harvests.find((h) => h.id === sale.harvest_id);
            if (!harvest || (harvest.plant_type_cl || harvest.plant_type) !== selectedPlantType) {
                return; // Skip if the sale's harvest doesn't match the selected plant type
            }

            const date = new Date(sale.date);
            const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

            if (!harvestData[monthYear]) {
                harvestData[monthYear] = {
                    monthYear,
                    harvestTotal: 0,
                    unitPriceSum: 0,
                    harvestCount: 0,
                };
            }

            const { unitPrice, harvestAmt } = salesByHarvestId[sale.harvest_id] || {};
            harvestData[monthYear].harvestTotal += harvestAmt || 0;
            harvestData[monthYear].unitPriceSum += (unitPrice || 0) * (harvestAmt || 0);
            harvestData[monthYear].harvestCount += harvestAmt || 0;
        });

        return Object.values(harvestData)
            .map((entry) => ({
                monthYear: entry.monthYear,
                harvestTotal: entry.harvestTotal,
                averageUnitPrice:
                    entry.harvestCount > 0
                        ? (entry.unitPriceSum / entry.harvestCount).toFixed(2)
                        : 0,
            }))
            .sort((a, b) => new Date(a.monthYear) - new Date(b.monthYear));
    };

    const stackedBarData = generateStackedBarData();
    const profitabilityData = generateProfitabilityData();
    //const salesData = generateSalesData();
    const harvestData = selectedPlantType ? generateHarvestData(selectedPlantType) : [];

    // Custom function to handle tab change
    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    // Gather all events for search filtering (shared between mobile and desktop)
    const allEvents = [
        ...(farmData.fertilizerEvents || []).map(e => ({ ...e, _type: 'fertilizer' })),
        ...(farmData.fungicideEvents || []).map(e => ({ ...e, _type: 'fungicide' })),
        ...(farmData.insecticideEvents || []).map(e => ({ ...e, _type: 'insecticide' })),
        ...(farmData.herbicideEvents || []).map(e => ({ ...e, _type: 'herbicide' })),
        ...(farmData.harvests || []).map(e => ({ ...e, _type: 'harvest' })),
        ...(farmData.sales || []).map(e => ({ ...e, _type: 'sale' })),
    ];
    
    // Filter events by date range (use global date range or fallback to past month)
    const now = new Date();
    const monthAgo = new Date(now);
    monthAgo.setDate(now.getDate() - 30);
    const filterStartDate = startDate || monthAgo;
    const filterEndDate = endDate || now;
    
    let filteredEvents = allEvents.filter(e => {
        const d = new Date(e.date);
        return d >= filterStartDate && d <= filterEndDate;
    });
    
    // Search filter (searches all stringified event details)
    if (search) {
        filteredEvents = filteredEvents.filter(e => {
            const str = JSON.stringify(e).toLowerCase();
            return str.includes(search.toLowerCase());
        });
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                bgcolor: "transparent",
                display: { xs: 'block', md: 'flex' },
                gap: { xs: 0, md: 2 },
                paddingLeft: { xs: 0, md: 2 },
                boxSizing: 'border-box',
                height: '100%',
            }}
        >
            {/* Main content for both mobile and desktop */}
            <Box
                sx={{
                    width: { xs: '100%', md: '65%' },
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    marginTop: { xs: 0, md: 1 },
                    px: { xs: 1, sm: 2, md: 0 },
                }}
            >
                {/* Date Range Filter for Metrics Cards */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: { xs: 'center', md: 'flex-end' }, 
                    mb: 2,
                    gap: 1,
                    px: { xs: 1, md: 0 }
                }}>
                    <Typography variant="caption" sx={{ 
                        color: '#AAB4C8', 
                        fontWeight: 500, 
                        mr: 1,
                        display: { xs: 'none', md: 'block' }
                    }}>
                        Filter metrics by date range:
                    </Typography>
                    <DatePicker
                        selected={startDate}
                        onChange={date => setStartDate(date)}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        maxDate={endDate}
                        dateFormat="MMM dd, yyyy"
                        placeholderText="Start Date"
                        customInput={<CustomDateInput placeholder="Start Date" height={32} />}
                        popperPlacement="bottom-start"
                    />
                    <DatePicker
                        selected={endDate}
                        onChange={date => setEndDate(date)}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        maxDate={new Date()}
                        dateFormat="MMM dd, yyyy"
                        placeholderText="End Date"
                        customInput={<CustomDateInput placeholder="End Date" height={32} />}
                        popperPlacement="bottom-start"
                    />
                </Box>

                {/* Metrics Cards */}
                <Grid container spacing={1} sx={{ width: '100%' }}>
                    {metrics.map((m, i) => (
                        <Grid item xs={12} md={3} key={i}>
                            <MetricsCard {...m} country={farmData.farmDetails?.country} />
                        </Grid>
                    ))}
                </Grid>

                {/* Charts: 2x2 grid on desktop, stacked on mobile */}
                <Grid container spacing={1.5} sx={{ width: '100%' }}>
                    <Grid item xs={12} md={6}>
                        {console.log('YieldTrendChart data:', {
                            cropsData: farmData.crops?.length,
                            harvestsData: farmData.harvests?.length,
                            zones: zones?.length,
                            zonesData: zones
                        })}
                        <YieldTrendChart 
                            cropsData={farmData.crops} 
                            harvestsData={farmData.harvests} 
                            zones={zones} 
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {console.log('InputUsageChart data:', {
                            fertilizerEvents: farmData.fertilizerEvents?.length,
                            fungicideEvents: farmData.fungicideEvents?.length,
                            insecticideEvents: farmData.insecticideEvents?.length,
                            herbicideEvents: farmData.herbicideEvents?.length,
                            crops: farmData.crops?.length,
                            sections: zones?.length,
                            sectionsData: zones
                        })}
                        <InputUsageChart
                            fertilizerEvents={farmData.fertilizerEvents || []}
                            fungicideEvents={farmData.fungicideEvents || []}
                            insecticideEvents={farmData.insecticideEvents || []}
                            herbicideEvents={farmData.herbicideEvents || []}
                            crops={farmData.crops || []}
                            sections={zones}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {console.log('HarvestsChart data:', {
                            harvestData: harvestData?.length,
                            selectedPlantType,
                            plantTypes: plantTypes?.length
                        })}
                        <HarvestsChart
                            data={harvestData}
                            selectedPlantType={selectedPlantType}
                            setSelectedPlantType={setSelectedPlantType}
                            plantTypes={plantTypes}
                        />
                    </Grid>
                </Grid>

                {/* Mobile: Activity Log and Current Crops below charts */}
                <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }}>
                    {/* Activity Log - Card wrapper to match desktop styling */}
                    <Card sx={{ 
                        mb: 2, 
                        maxHeight: 650, 
                        overflow: 'hidden', 
                        backgroundColor: '#181F2A', 
                        borderRadius: 1.5, 
                        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)', 
                        p: 0, 
                        mt: 1,
                        mr: 2,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {/* Sticky Header */}
                        <Box sx={{ 
                            position: 'sticky', 
                            top: 0, 
                            zIndex: 10, 
                            pt: 3, 
                            px: 3, 
                            pb: 0.5,
                            borderBottom: '1px solid rgba(255,255,255,0.1)'
                        }}>
                            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 500, fontSize: 15, mb: 1 }}>
                                Activity Log
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                pt: 1,
                                pb: 1,
                            }}>
                                <SearchIcon sx={{ color: '#AAB4C8', mr: 1 }} />
                                <InputBase
                                    placeholder="Search events..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    sx={{
                                        color: '#fff',
                                        bgcolor: alpha('#222C2F', 0.8),
                                        borderRadius: 2,
                                        px: 2,
                                        py: 0.5,
                                        fontSize: 14,
                                        width: '100%',
                                    }}
                                    inputProps={{ 'aria-label': 'search events' }}
                                />
                            </Box>
                        </Box>
                        
                        {/* Scrollable Content */}
                        <Box sx={{ flex: 1, overflow: 'auto', px: 0, pb: 2 }}>
                            <TimelineEvents
                                fertilizerEvents={filteredEvents.filter(e => e._type === 'fertilizer')}
                                fungicideEvents={filteredEvents.filter(e => e._type === 'fungicide')}
                                insecticideEvents={filteredEvents.filter(e => e._type === 'insecticide')}
                                herbicideEvents={filteredEvents.filter(e => e._type === 'herbicide')}
                                harvests={filteredEvents.filter(e => e._type === 'harvest')}
                                sales={filteredEvents.filter(e => e._type === 'sale')}
                                startDate={startDate}
                                endDate={endDate}
                            />
                        </Box>
                    </Card>
                    
                    {/* Current Crops Table */}
                    <CurrentCropsTable
                        crops={farmData.crops}
                        zones={farmData.zones}
                        harvests={farmData.harvests}
                    />
                </Box>
            </Box>

            {/* Desktop: Right side - Activity Log and Current Crops */}
            <Box sx={{
                width: { xs: '100%', md: '35%' },
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                gap: 1,
                height: '100%',
                position: 'sticky',
                top: 2,
                paddingRight: 2,
            }}>
                {/* Searchable, scrollable TimelineEvents above CurrentCropsTable */}
                {(() => {
                    return (
                        <Card sx={{ 
                            mb: 2, 
                            maxHeight: 656, 
                            overflow: 'hidden', 
                            backgroundColor: '#181F2A', 
                            borderRadius: 1.5, 
                            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)', 
                            p: 0, 
                            mt: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            mr: 2,
                        }}>
                            {/* Sticky Header */}
                            <Box sx={{ 
                                position: 'sticky', 
                                top: 0, 
                                zIndex: 10, 
                                pt: 3, 
                                px: 3, 
                                pb: 0.5,
                                borderBottom: '1px solid rgba(255,255,255,0.1)'
                            }}>
                                <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 500, fontSize: 15, mb: 1 }}>
                                    Activity Log
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    pt: 1,
                                    pb: 1,
                                }}>
                                    <SearchIcon sx={{ color: '#AAB4C8', mr: 1 }} />
                                    <InputBase
                                        placeholder="Search events..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        sx={{
                                            color: '#fff',
                                            bgcolor: alpha('#222C2F', 0.8),
                                            borderRadius: 2,
                                            px: 2,
                                            py: 0.5,
                                            fontSize: 14,
                                            width: '100%',
                                        }}
                                        inputProps={{ 'aria-label': 'search events' }}
                                    />
                                </Box>
                            </Box>
                            
                            {/* Scrollable Content */}
                            <Box sx={{ flex: 1, overflow: 'auto', px: 0, pb: 2 }}>
                                <TimelineEvents
                                    fertilizerEvents={filteredEvents.filter(e => e._type === 'fertilizer')}
                                    fungicideEvents={filteredEvents.filter(e => e._type === 'fungicide')}
                                    insecticideEvents={filteredEvents.filter(e => e._type === 'insecticide')}
                                    herbicideEvents={filteredEvents.filter(e => e._type === 'herbicide')}
                                    harvests={filteredEvents.filter(e => e._type === 'harvest')}
                                    sales={filteredEvents.filter(e => e._type === 'sale')}
                                    startDate={startDate}
                                    endDate={endDate}
                                />
                            </Box>
                        </Card>
                    );
                })()}
                <Box sx={{ maxHeight: 650, overflow: 'auto' }}>
                    <CurrentCropsTable
                        crops={farmData.crops}
                        zones={farmData.zones}
                        harvests={farmData.harvests}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default FarmDetailsPage;
